import { webEnGB } from './web-en-GB.js';
import { settingsEnGB } from './settings-en-GB.js';
import { phase1EnGB } from './phase1/index.js';
import { phase2EnGB } from './phase2/index.js';
import { phase3EnGB } from './phase3/index.js';
import { phase4EnGB } from './phase4/index.js';
import { phase5EnGB } from './phase5/index.js';
/**
 * English (UK) messages. Flat keys, `{name}` placeholders.
 * Every user-facing string in server emails/errors, the portal and the apps comes from here
 * (or from a module's own message file merged in later). Other languages copy these keys.
 */
export const enGB = {
    // Product
    'app.name': 'CloudSign',
    'app.kioskName': 'CloudSign Kiosk',
    // Errors (ApiError.messageKey)
    'error.validation': 'Some details need correcting.',
    'error.unauthenticated': 'Please sign in.',
    'error.mfa_required': 'Please enter your two-factor code.',
    'error.forbidden': "You don't have permission to do that.",
    'error.not_found': 'Not found.',
    'error.conflict': 'That conflicts with something that already exists.',
    'error.rate_limited': 'Too many attempts. Please wait a moment and try again.',
    'error.module_disabled': 'This feature is not switched on for your organisation.',
    'error.tenant_suspended': 'This organisation is suspended. Please contact your administrator.',
    'error.internal': 'Something went wrong. Please try again.',
    'error.login_failed': 'Email or password is incorrect.',
    'error.account_locked': 'Too many failed attempts. Try again in {minutes} minutes.',
    'error.totp_invalid': 'That code is not right. Please try again.',
    'error.password_too_short': 'Use at least {min} characters.',
    'error.password_reuse': 'Choose a password you have not used before.',
    'error.token_invalid': 'This link has expired or has already been used.',
    'error.slug_taken': 'That address is already in use.',
    'error.email_in_tenant': 'That person is already a user here.',
    'error.role_in_use': 'This role is still given to {count} users.',
    'error.role_builtin': 'Built-in roles cannot be changed or deleted.',
    'error.role_escalation': 'You cannot give permissions you do not have yourself.',
    'error.site_escalation': 'You can only give access to sites that you have access to yourself.',
    'error.last_admin': 'There must always be at least one active administrator.',
    'error.shared_account': "This person uses CloudSign with another organisation, so their two-factor can't be removed here.",
    'error.2fa_required': 'Your organisation requires two-factor authentication.',
    // Auth screens
    'auth.signIn': 'Sign in',
    'auth.signOut': 'Sign out',
    'auth.email': 'Email address',
    'auth.password': 'Password',
    'auth.forgotPassword': 'Forgotten your password?',
    'auth.forgotSent': 'If that email has an account, we have sent a link to reset the password.',
    'auth.resetPassword': 'Set a new password',
    'auth.newPassword': 'New password',
    'auth.passwordRule': 'At least {min} characters. A short phrase of a few words is easy to remember.',
    'auth.currentPassword': 'Current password',
    'auth.changePassword': 'Change password',
    'auth.totpTitle': 'Two-factor authentication',
    'auth.totpPrompt': 'Enter the 6-digit code from your authenticator app.',
    'auth.totpCode': 'Code',
    'auth.useRecoveryCode': 'Use a recovery code instead',
    'auth.recoveryCode': 'Recovery code',
    'auth.totpSetupTitle': 'Set up two-factor authentication',
    'auth.totpSetupPrompt': 'Scan this QR code with an authenticator app, then enter the code it shows.',
    'auth.totpManualKey': 'Or enter this key: {secret}',
    'auth.recoveryCodesTitle': 'Your recovery codes',
    'auth.recoveryCodesPrompt': 'Keep these somewhere safe. Each one works once if you lose your phone.',
    'auth.chooseTenant': 'Choose an organisation',
    'auth.platformArea': 'Platform administration',
    'auth.acceptInvite': 'Accept invitation',
    'auth.inviteTitle': 'You have been invited to {tenant}',
    'auth.fullName': 'Full name',
    'auth.twoFactorOn': 'Two-factor authentication is on.',
    'auth.twoFactorOff': 'Two-factor authentication is off.',
    'auth.turnOn2fa': 'Turn on two-factor',
    'auth.turnOff2fa': 'Turn off two-factor',
    // Navigation
    'nav.section.main': 'Main',
    'nav.section.admin': 'Administration',
    'nav.section.platform': 'Platform',
    'nav.section.account': 'My account',
    'nav.home': 'Home',
    'nav.users': 'Users',
    'nav.roles': 'Roles',
    'nav.settings': 'Settings',
    'nav.emails': 'Email log',
    'nav.audit': 'Audit log',
    'nav.account': 'My account',
    'nav.platform.tenants': 'Customers',
    'nav.platform.admins': 'Platform admins',
    'nav.platform.settings': 'Platform settings',
    'nav.platform.jobs': 'Background jobs',
    'nav.platform.audit': 'Platform audit log',
    // Common UI
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.search': 'Search',
    'common.loading': 'Loading…',
    'common.noResults': 'Nothing to show.',
    'common.saved': 'Saved.',
    'common.export': 'Export',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.status': 'Status',
    'common.name': 'Name',
    'common.actions': 'Actions',
    'common.page': 'Page {page} of {pages}',
    'common.default': 'Default: {value}',
    // Statuses
    'status.invited': 'Invited',
    'status.active': 'Active',
    'status.disabled': 'Disabled',
    'status.suspended': 'Suspended',
    'status.archived': 'Archived',
    'status.queued': 'Queued',
    'status.sending': 'Sending',
    'status.running': 'Running',
    'status.sent': 'Sent',
    'status.done': 'Done',
    'status.failed': 'Failed',
    // Roles
    'role.administrator': 'Administrator',
    'role.standard': 'Standard',
    'role.siteScope.all': 'All sites',
    'role.siteScope.assigned': 'Only assigned sites',
    // Settings labels (core)
    'settings.core.branding': 'Branding',
    'settings.core.branding.displayName': 'Display name',
    'settings.core.branding.logo': 'Logo',
    'settings.core.branding.primaryColour': 'Main colour',
    'settings.core.branding.accentColour': 'Accent colour',
    'settings.core.branding.emailSenderName': 'Email sender name',
    'settings.core.branding.emailReplyTo': 'Email reply-to address',
    'settings.core.locale': 'Language and time',
    'settings.core.locale.defaultLanguage': 'Default language',
    'settings.core.locale.timeZone': 'Default time zone',
    'settings.core.security': 'Security',
    'settings.core.security.require2fa': 'Require two-factor authentication for all users',
    'settings.core.security.sessionIdleMinutes': 'Sign out after this many idle minutes (0 = platform default)',
    'settings.core.security.allowSupportAccess': 'Allow CloudSign support to sign in as an administrator',
    'settings.core.email': 'Email',
    'settings.core.email.logRetentionDays': 'Keep the email log for this many days (0 = forever)',
    // Emails
    'email.footer': 'Sent by {sender} using CloudSign.',
    'email.invite.subject': 'You have been invited to {tenant} on CloudSign',
    'email.invite.body': '{inviter} has invited you to use CloudSign for {tenant}. This link works for {hours} hours.',
    'email.invite.button': 'Accept invitation',
    'email.added.subject': 'You now have access to {tenant} on CloudSign',
    'email.added.body': 'You have been given access to {tenant}. Sign in with your existing CloudSign account.',
    'email.added.button': 'Sign in',
    'email.reset.subject': 'Reset your CloudSign password',
    'email.reset.body': 'Use this link to set a new password. It works for {minutes} minutes. If you did not ask for this, you can ignore this email.',
    'email.reset.button': 'Set a new password',
    'email.2faRemoved.subject': 'Two-factor authentication was removed from your CloudSign account',
    'email.2faRemoved.body': 'An administrator of {tenant} removed two-factor authentication from your account. You will be asked to set it up again if it is required.',
    // Mobile shells
    'mobile.serverOk': 'Connected to CloudSign {version}',
    'mobile.serverDown': 'Cannot reach the CloudSign server',
    'mobile.kioskPlaceholder': 'Welcome. This kiosk is not set up yet.',
    'mobile.companionPlaceholder': 'Welcome to CloudSign.',
    'mobile.retry': 'Try again',
    // Modules
    'module.core': 'Core',
    // Permission groups
    'permGroup.audit': 'Audit log',
    'permGroup.today': 'Today',
    'permGroup.evacuation': 'Evacuation',
    'permGroup.history': 'History',
    'permGroup.timesheets': 'Timesheets',
    'permGroup.spaces': 'Spaces',
    'permGroup.sites': 'Sites',
    'permGroup.events': 'Events',
    'permGroup.groups': 'Groups',
    'permGroup.visitors': 'Visitors',
    'permGroup.api_clients': 'Client API',
    'permGroup.forms': 'Pre-registration forms',
    'permGroup.notifications': 'Notifications',
    'permGroup.users': 'Portal users',
    'permGroup.account': 'Account settings',
    'permGroup.safety_check': 'Safety check',
    'permGroup.roles': 'Roles',
    'permGroup.deliveries': 'Deliveries',
    'permGroup.attendance': 'Time and attendance',
    'permGroup.scheduling': 'Scheduling',
    'permGroup.webhooks': 'Webhooks',
    'permGroup.integrations': 'Integrations',
    'permGroup.data_protection': 'Data protection',
    'permGroup.compliance': 'Compliance documents',
    'permGroup.inductions': 'Inductions',
    'permGroup.permits': 'Permits to work',
    'permGroup.lone_working': 'Lone working',
    'permGroup.id_checks': 'ID checks',
    'permGroup.displays': 'Reception displays',
    'permGroup.feedback': 'Visitor feedback',
    'permGroup.insights': 'Trends and insights',
    'permGroup.ask_reports': 'Ask-a-question reports',
    'permGroup.devices': 'Devices',
    'permGroup.notices': 'Notices',
    'permGroup.badges': 'Badges',
    'permGroup.agreements': 'Signed agreements',
    'permGroup.platform': 'Platform administration',
    'permGroup.partner': 'Partner administration',
    // Permissions
    'perm.audit.view': 'View the audit log',
    'perm.today.view': 'View the today page',
    'perm.today.view_locations': 'View visitor locations',
    'perm.today.sign_in_out': 'Sign visitors in and out',
    'perm.today.approve_preregistrations': 'Approve pre-registrations',
    'perm.today.sign_out_all': 'Sign everyone out',
    'perm.evacuation.view': 'View the evacuation list',
    'perm.evacuation.reports_view': 'View evacuation reports',
    'perm.evacuation.reports_manage': 'Create and edit evacuation reports',
    'perm.evacuation.reports_delete': 'Delete evacuation reports',
    'perm.history.view': 'View history',
    'perm.history.export': 'Export history',
    'perm.history.delete': 'Delete visit records',
    'perm.history.protect': 'Protect visit records',
    'perm.timesheets.view': 'View timesheets',
    'perm.timesheets.export': 'Export timesheets',
    'perm.timesheets.edit': 'Edit timesheet records',
    'perm.spaces.view': 'View spaces',
    'perm.spaces.manage': 'Manage spaces',
    'perm.spaces.delete': 'Delete spaces',
    'perm.spaces.book': 'Book spaces',
    'perm.spaces.override_zones': 'Override zone restrictions',
    'perm.spaces.bookings_report': 'View and export the bookings report',
    'perm.sites.view': 'View sites',
    'perm.sites.manage_devices': 'Manage devices',
    'perm.sites.manage': 'Manage sites',
    'perm.sites.delete_remote': 'Delete remote sites',
    'perm.events.view': 'View events',
    'perm.events.manage': 'Manage events',
    'perm.events.delete': 'Delete events',
    'perm.groups.view': 'View groups',
    'perm.groups.manage': 'Manage groups',
    'perm.groups.delete': 'Delete groups',
    'perm.groups.export_members': 'Export members',
    'perm.groups.view_members': 'View members',
    'perm.groups.manage_members': 'Manage members',
    'perm.groups.delete_members': 'Delete members',
    'perm.groups.manage_sensitive': 'Manage sensitive data',
    'perm.visitors.edit_notes': 'Edit visitor notes',
    'perm.visitors.edit_visits': 'Edit visits',
    'perm.api_clients.view': 'View API clients',
    'perm.api_clients.manage': 'Manage API clients',
    'perm.api_clients.delete': 'Delete API clients',
    'perm.forms.view': 'View pre-registration forms',
    'perm.forms.manage': 'Manage pre-registration forms',
    'perm.forms.delete': 'Delete pre-registration forms',
    'perm.notifications.view': 'View notifications',
    'perm.notifications.manage': 'Manage notifications',
    'perm.notifications.delete': 'Delete notifications',
    'perm.users.view': 'View portal users',
    'perm.users.manage': 'Manage portal users',
    'perm.users.delete': 'Delete portal users',
    'perm.users.remove_2fa': 'Remove two-factor authentication',
    'perm.account.manage': 'Manage account settings',
    'perm.safety_check.view': 'View block list entries',
    'perm.safety_check.manage': 'Manage block list entries',
    'perm.safety_check.delete': 'Delete block list entries',
    'perm.roles.manage': 'Manage roles',
    'perm.deliveries.view': 'View deliveries',
    'perm.deliveries.manage': 'Manage deliveries',
    'perm.attendance.view': 'View time and attendance',
    'perm.attendance.manage': 'Manage time and attendance',
    'perm.scheduling.view': 'View schedules',
    'perm.scheduling.manage': 'Manage schedules',
    'perm.webhooks.view': 'View webhooks',
    'perm.webhooks.manage': 'Manage webhooks',
    'perm.webhooks.delete': 'Delete webhooks',
    'perm.integrations.view': 'View integrations',
    'perm.integrations.manage': 'Manage integrations',
    'perm.data_protection.export': 'Export personal data (subject access)',
    'perm.data_protection.erase': 'Erase personal data',
    'perm.data_protection.manage_retention': 'Manage data retention',
    'perm.compliance.view': 'View compliance documents',
    'perm.compliance.manage': 'Manage compliance documents',
    'perm.inductions.view': 'View inductions',
    'perm.inductions.manage': 'Manage inductions',
    'perm.permits.view': 'View permits',
    'perm.permits.raise': 'Raise permits',
    'perm.permits.issue': 'Issue permits',
    'perm.permits.close': 'Close permits',
    'perm.lone_working.view': 'View lone workers',
    'perm.lone_working.respond': 'Respond to lone-worker escalations',
    'perm.id_checks.view_result': 'View ID check results',
    'perm.id_checks.view_image': 'View ID document images',
    'perm.displays.view': 'View reception displays',
    'perm.displays.manage': 'Manage reception displays',
    'perm.feedback.view': 'View visitor feedback',
    'perm.feedback.manage': 'Manage visitor feedback',
    'perm.insights.view': 'View trends and insights',
    'perm.ask_reports.use': 'Use ask-a-question reports',
    'perm.devices.view': 'View devices and their health',
    'perm.devices.remote_actions': 'Use device remote actions',
    'perm.notices.view': 'View notices',
    'perm.notices.manage': 'Manage notices and where they are shown',
    'perm.notices.delete': 'Delete notices',
    'perm.badges.manage': 'Manage badge templates',
    'perm.agreements.view': 'View and download signed agreements',
    'perm.agreements.manage': 'Retry failed agreements and record chain checks',
    'perm.platform.tenants_view': 'View customers',
    'perm.platform.tenants_manage': 'Create and manage customers',
    'perm.platform.modules_manage': 'Switch modules on and off for customers',
    'perm.platform.settings_manage': 'Manage platform settings',
    'perm.platform.admins_manage': 'Manage platform admins',
    'perm.platform.audit_view': 'View the platform audit log',
    'perm.platform.jobs_view': 'View background jobs',
    'perm.platform.jobs_manage': 'Retry background jobs',
    'perm.platform.support_access': 'Sign in as a customer administrator (with consent)',
    'perm.partner.tenants_view': "View the partner's customers",
    'perm.partner.tenants_manage': "Manage the partner's customers",
    'perm.partner.modules_manage': "Switch modules for the partner's customers",
    'perm.partner.support_access': "Sign in as a partner customer's administrator (with consent)",
    // Portal (web) messages live in web-en-GB.ts
    ...webEnGB,
    // Settings labels and settings/branding/files/platform messages live in settings-en-GB.ts
    ...settingsEnGB,
    // Phase 1: one file per task in ./phase1/
    ...phase1EnGB,
    // Phase 2: one file per task in ./phase2/
    ...phase2EnGB,
    // Phase 3: one file per task in ./phase3/
    ...phase3EnGB,
    // Phase 4: one file per task in ./phase4/
    ...phase4EnGB,
    // Phase 5: one file per task in ./phase5/ (shared reception: docs/SHARED-RECEPTION-PLAN.md)
    ...phase5EnGB,
};
//# sourceMappingURL=en-GB.js.map